/**
 * Provider-neutral validation for the join link that goes into reminder templates.
 *
 * Templates mark `appointment.videoLink` required, so anything this rejects suppresses the
 * send rather than mailing a dead link. Accepts only a Unify `/join/<code>` link on our own
 * public origin — never an arbitrary URL, since the value
 * comes off a FHIR extension that other writers could touch.
 */

import { getPublicAppBaseUrl } from '@/lib/publicAppBaseUrl';

export function isUnifyJoinUrl(url: string, appBaseUrl: string): boolean {
  try {
    const parsed = new URL(url);
    const expected = new URL(appBaseUrl);
    if (parsed.protocol !== 'https:' && parsed.hostname !== 'localhost') return false;
    // Full origin (scheme + host + port), not just the hostname.
    if (parsed.origin !== expected.origin) return false;
    // The stored link is plain, with no query or fragment.
    if (parsed.search || parsed.hash || url.includes('?') || url.includes('#')) return false;
    // /join/<code> and nothing deeper. The retired signed `<payload>.<sig>` links no
    // longer open a session, so they are rejected rather than mailed.
    return /^\/join\/[A-Za-z0-9_-]+$/.test(parsed.pathname);
  } catch {
    return false;
  }
}

export function getVideoJoinHref(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  const trimmed = url.trim();
  return isUnifyJoinUrl(trimmed, getPublicAppBaseUrl()) ? trimmed : undefined;
}

